import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@/components/ui/Button'
import { CompanyBrandingForm, type CompanyBrandingValue } from '@/components/CompanyBrandingForm'
import { CREATE_TEAM, UPDATE_TEAM, CREATE_TEAM_BRANDING, CREATE_TEAM_BRANDING_ASSET, CREATE_TEAM_ADMIN } from '@/graphql/mutations/teams'
import { INTERNAL_ADMIN_PROFILE_IDS } from '@/lib/constants'
import { useAuthStore } from '@/stores/auth'
import { apolloClient } from '@/lib/apollo'
import { markTeamSwitchedForChargebee } from '@/lib/chargebee'
import { notifyNewAccountCreated } from '@/lib/newAccount'

interface AddAccountModalProps {
  onClose: () => void
}

// Lets an internal admin (sales) stand up a brand-new top-level team ahead of a prospective
// client's own self-serve signup, so it can be demoed before they sign up themselves. Mirrors
// SignupPage's "Company Details" step + team/branding creation, minus anything tied to the
// signing-up user themselves: no personal sub-team, no teamAdmins row for the calling admin (they
// reach the new account via the Switch Account search, same as any other team), and no trial
// webhook — trial start is something the real customer goes through themselves later.
export function AddAccountModal({ onClose }: AddAccountModalProps) {
  const setSelectedTeamId = useAuthStore((s) => s.setSelectedTeamId)
  const [companyBranding, setCompanyBranding] = useState<CompanyBrandingValue>({
    companyURL: '',
    companyName: '',
    logoURL: null,
    heroImageURL: null,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createTeam] = useMutation(CREATE_TEAM)
  const [updateTeam] = useMutation(UPDATE_TEAM)
  const [createBranding] = useMutation(CREATE_TEAM_BRANDING)
  const [createBrandingAsset] = useMutation(CREATE_TEAM_BRANDING_ASSET)
  const [createTeamAdmin] = useMutation(CREATE_TEAM_ADMIN)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const { companyName, companyURL, logoURL, heroImageURL } = companyBranding
    if (!companyName.trim()) {
      setError('Enter a company name to continue.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const { data } = await createTeam({
        variables: {
          newTeam: {
            name: companyName,
            topLevelTeam: true,
            teamLogoURL: logoURL,
            websiteURL: companyURL.trim() || undefined,
            tags: ['Brand'],
          },
        },
      })
      if (data?.createTeam?.returnCode !== 0 || !data?.createTeam?.id) {
        setError(data?.createTeam?.message || 'Failed to create account. Please try again.')
        return
      }
      const teamId = data.createTeam.id as string

      // Rolla staff get added as internal admins, same as front-door signup, so support/CS has
      // access without a separate invite — best-effort, a demo account being created shouldn't be
      // blocked by this failing.
      for (const profileId of INTERNAL_ADMIN_PROFILE_IDS) {
        createTeamAdmin({
          variables: { newTeamAdmin: { teamId, profileId, primary: false, role: 'internal' } },
        }).catch(() => {})
      }

      // Mirrors SignupPage's handleCompanyContinue — seed a teamBranding + teamBrandingAsset row
      // so the branded web app has something to show immediately during a demo.
      // teamBrandings.teamId (the reverse FK) is deprecated — teams.teamBrandingId, set via the
      // checked updateTeam call below, is the current, canonical link.
      if (logoURL || heroImageURL) {
        const { data: brandingData } = await createBranding({
          variables: { newTeamBranding: { name: companyName, signInOptions: ['email', 'google'] } },
        })
        const brandingId = brandingData?.createTeamBranding?.id
        if (brandingId) {
          const [linkResult] = await Promise.all([
            updateTeam({ variables: { changedTeam: { id: teamId, teamBrandingId: brandingId } } }),
            createBrandingAsset({
              variables: {
                newTeamBrandingAsset: {
                  teamBrandingId: brandingId,
                  logoNoPoweredByLargeURL: logoURL ?? undefined,
                  logoDarkPoweredByLargeURL: logoURL ?? undefined,
                  logoLightPoweredByLargeURL: logoURL ?? undefined,
                  heroImageURL: heroImageURL ?? undefined,
                },
              },
            }),
          ])
          if (linkResult.data?.updateTeam?.returnCode !== 0) {
            throw new Error(linkResult.data?.updateTeam?.message || 'Failed to link branding to team')
          }
        }
      }

      notifyNewAccountCreated({
        type: 'internalAdmin',
        topLevelTeamId: teamId,
        topLevelTeamName: companyName,
        logoURL,
      })

      // Same switch mechanism as BrandSwitcher's handleSelect — drop the admin straight into the
      // new account so they can start demoing it immediately.
      setSelectedTeamId(teamId)
      apolloClient.resetStore()
      markTeamSwitchedForChargebee()
      onClose()
    } catch {
      setError('Failed to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Account</h2>
          <p className="mt-1 text-sm text-gray-500">Stand up a new account to demo before a client signs up.</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <CompanyBrandingForm
            value={companyBranding}
            onChange={setCompanyBranding}
            uploadNamespace="add-account"
            onError={(message) => setError(message || null)}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
